import { useEffect, useState } from 'react'
import { authorizedBlob } from '../lib/api.js'
import { useFiles } from '../lib/items.js'
import type { FileRow } from '../lib/items.js'
import { Icon } from './Icon.js'

/** Thumbnails need the bearer token, which an <img src> cannot send — fetched as a blob instead. */
function Thumb({ file }: { file: FileRow }): JSX.Element {
  const [src, setSrc] = useState<string>()
  useEffect(() => {
    if (!file.thumb) return
    let url: string | undefined
    let alive = true
    void authorizedBlob(file.thumb).then((blob) => {
      if (!alive || !blob) return
      url = URL.createObjectURL(blob)
      setSrc(url)
    })
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [file.thumb])

  const video = file.mimeType.startsWith('video/')
  return (
    <a
      href={file.webUrl}
      target="_blank"
      rel="noreferrer noopener"
      title={file.name}
      className="group relative block aspect-square overflow-hidden rounded-[12px] bg-fill/[0.1]"
    >
      {src ? (
        <img src={src} alt={file.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
      ) : (
        <span className="grid h-full w-full place-items-center p-2 text-center text-[10px] leading-tight text-label2/60">{file.name}</span>
      )}
      {video && (
        <span className="absolute bottom-1.5 right-1.5 grid h-6 w-6 place-items-center rounded-full bg-black/55 text-[10px] text-white" aria-label="Video">
          ▶
        </span>
      )}
    </a>
  )
}

/** What is already in the entry's folder — read-only mirror of SharePoint (CLAUDE.md §3.2). */
export function ItemFiles({ itemId, hasPath }: { itemId: string; hasPath: boolean }): JSX.Element | null {
  const { data } = useFiles(itemId)
  if (!data) return null
  if (!data.configured) {
    return hasPath ? (
      <p className="flex items-center gap-2 rounded-[14px] bg-fill/[0.06] px-4 py-3 text-caption text-label2/60">
        <Icon name="folder" size={16} />
        Bild-Vorschauen erscheinen hier, sobald die Microsoft-365-Freigabe eingerichtet ist.
      </p>
    ) : null
  }
  if (data.files.length === 0) {
    return hasPath ? <p className="text-caption text-label2/50">Im Ordner liegen noch keine Dateien.</p> : null
  }
  return (
    <section>
      <p className="section-title px-0">
        Dateien <span className="num font-normal text-label2/50">{data.files.length}</span>
      </p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {data.files.slice(0, 24).map((f) => (
          <Thumb key={f.id} file={f} />
        ))}
      </div>
    </section>
  )
}
